import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../services/auth';
import './Login.css';

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaPendingCredential, setMfaPendingCredential] = useState('');
  const [mfaEnrollmentId, setMfaEnrollmentId] = useState('');

  const mfaInputRef = useRef(null);

  useEffect(() => {
    if (mfaRequired && mfaInputRef.current) {
      mfaInputRef.current.focus();
    }
  }, [mfaRequired]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await auth.login(email, password);

      if (result.status === 'success') {
        onLoginSuccess();
        window.location.assign('/overview');
      } else if (result.status === 'mfa_required') {
        setMfaPendingCredential(result.mfaPendingCredential);
        setMfaEnrollmentId(result.mfaEnrollmentId);
        setMfaCode('');
        setMfaRequired(true);
      } else {
        setError(result.message || 'Invalid email or password');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedCode = mfaCode.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);

    try {
      const result = await auth.mfaVerifyFinalize(
        mfaPendingCredential,
        mfaEnrollmentId,
        trimmedCode,
      );

      if (result.status === 'success') {
        onLoginSuccess();
        window.location.assign('/overview');
      } else {
        setError(result.message || 'MFA verification failed. Please try again.');
        setMfaCode('');
      }
    } catch (err) {
      setError('An error occurred during MFA verification');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaCode('');
    setMfaPendingCredential('');
    setMfaEnrollmentId('');
    setError('');
  };

  if (mfaRequired) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h2>Two-Factor Authentication</h2>
          <p className="login-mfa-hint">
            Enter the 6-digit code from your authenticator app.
          </p>
          <form onSubmit={handleMfaSubmit}>
            <div className="form-group">
              <label htmlFor="mfa-code">Verification Code</label>
              <input
                ref={mfaInputRef}
                type="text"
                id="mfa-code"
                value={mfaCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setMfaCode(val);
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                required
              />
            </div>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
          <button
            type="button"
            className="login-back-button"
            onClick={handleBackToLogin}
            disabled={loading}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Login</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
